import {
	Button,
	ButtonGroup,
	Card,
	IconButton,
	LuIcon,
	Menu,
	SegmentedTabs,
	Separator,
	Text,
	toast,
} from "@overmap-ai/blocks"
import {
	type Asset,
	type Form,
	type FormSubmission,
	type Issue,
	type IssueAttachment,
	OrganizationAccessLevel,
	type IssueTypeFields,
	type Stored,
	submittedAtModelSortFn,
	toUuidArray,
	EMPTY_ARRAY,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import {
	APIError,
	selectAssetsOfProject,
	selectAttachmentsOfIssue,
	selectAttachmentsOfIssueTypeFieldValues,
	selectFormsOfOrganization,
	selectFormSubmissionsOfIssue,
	selectFormSubmissionsOfProject,
	selectIssueAssociationsOfIssue,
	selectIssueById,
	selectIssueTypeById,
	selectIssueTypeIdentifiersOfIssueType,
	selectIssueTypePrioritiesOfIssueType,
	selectIssueTypeStatusesOfIssueType,
	selectLatestIssueTypeFieldsOfIssueType,
	selectIssueTypeFieldsById,
	selectIssueTypeFieldsOfIssueType,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { type FieldValues } from "@overmap-ai/forms"
import { useOpenState, useSort, useSyncedState } from "@overmap-ai/hooks"
import { useNavigate } from "@tanstack/react-router"
import type { ChainedCommands } from "@tiptap/core"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import * as turf from "@turf/turf"
import { type FocusEventHandler, memo, useCallback } from "react"

import { TITLE_MAX_LENGTH } from "../../constants"
import { useLocationPicker } from "../../contexts/hooks"
import { useAssetsWithIdentifiers } from "../../hooks/assets"
import { useFormSubmissionsWithIdentifiers } from "../../hooks/forms"
import { useDeleteIssue, useIssue } from "../../hooks/issues"
import { useAssignees, useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput, useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectActiveOrganizationId, selectActiveProjectId } from "../../store/selectors"
import { selectLatestIssueTypeFieldValuesOfIssue } from "../../store/selectors/issues"
import {
	BASE_EXTENSIONS,
	IssueAttachmentNode,
	LINK_NODES,
	MENTION_EXTENSIONS,
	useEditorBlur,
	useEditorContent,
} from "../../tiptap"
import { NewLineFloatingMenu, TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { IssueFileHandler } from "../../tiptap/extensions"
import { BaseAttachmentNode } from "../../tiptap/nodes/BaseAttachment/extension"
import type { IssuePdfExportOptions } from "../../typings/reports"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { AttachmentList } from "../AttachmentList/AttachmentList"
import { AttachmentButton } from "../Buttons/AttachmentButton"
import { AssetCommandMenu } from "../CommandMenus/AssetCommandMenu"
import { FormSubmissionCommandMenu } from "../CommandMenus/FormSubmissionCommandMenu"
import { IssuePdfExportOptionsDialog } from "../Dialogs/IssuePdfExportOptionsDialog"
import { IssueTypeDescriptionDialog } from "../Dialogs/IssueTypeDescriptionDialog"
import { AssigneeSelect } from "../DropdownSelects/AssigneeSelect"
import { FieldsRevisionDropdownSelect } from "../DropdownSelects/FieldsRevisionDropdownSelect"
import { IssuePrioritySelect } from "../DropdownSelects/IssuePrioritySelect"
import { IssueTypeStatusSelect } from "../DropdownSelects/IssueTypeStatusSelect"
import {
	useDeserializeFieldSchema,
	useDeserializeFieldValues,
	useFieldValuesChangeDebounced,
	useHydrateFieldValuesWithAttachments,
} from "../Form/hooks"
import { IssueTypeFieldsSubmitter } from "../Form/IssueTypeFieldsSubmitter"
import {
	getAttachmentPayloadsFromFilesAndPromises,
	getFieldFilesAndPromisesToCreate,
	getFieldValuesAttachmentsToDelete,
	getIsFieldValuesDraft,
} from "../Form/utils"
import { FormSubmissionList } from "../FormSubmissionList/FormSubmissionList"
import { IssuePriorityIcon } from "../Icons/IssuePriorityIcon"
import { IssueTypeIcon } from "../Icons/IssueTypeIcon"
import { IssueTypeStatusIcon } from "../Icons/IssueTypeStatusIcon"
import { UserIcon } from "../Icons/UserIcon"
import { IssueIndexLabel } from "../IndexLabels/IssueIndex"
import { IssueAssociationList } from "../IssueAssociationList/IssueAssociationList"
import { IssueTimeline } from "../IssueTimeline/IssueTimeline"
import { IssueTypeIdentifiersList } from "../IssueTypeIdentifiersList/IssueTypeIdentifiersList"
import { LocationGroup } from "../MenuGroups/LocationGroup"
import { FileInputMenuItem } from "../MenuItems/FileInputMenuItem"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"
import { DayPickerPopover } from "../Popovers/DayPickerPopover"

enum IssueDetailsViewerTabs {
	OVERVIEW = "overview",
	ATTACHMENTS = "attachments",
}

export interface IssueDetailsViewerProps {
	issueId: Issue["uuid"]
}

export const IssueDetailsViewer = memo((props: IssueDetailsViewerProps) => {
	const { issueId } = props

	const issue = useAppSelector(selectIssueById(issueId))

	if (!issue) {
		throw new Error("issue is unexpectedly undefined")
	}

	const issueType = useAppSelector(selectIssueTypeById(issue.issue_type))

	if (!issueType) {
		throw new Error(`Issue type ${issue.issue_type} not found`)
	}

	const latestIssueTypeFields = useAppSelector(selectLatestIssueTypeFieldsOfIssueType(issueType.uuid))
	const latestIssueTypeFieldValues = useAppSelector(selectLatestIssueTypeFieldValuesOfIssue(issue.uuid))
	const activeProjectId = useAppSelector(selectActiveProjectId)!
	const activeOrganizationId = useAppSelector(selectActiveOrganizationId)!
	const latestIssueTypeFieldValuesAttachments = useShallowEqaulSelector(
		selectAttachmentsOfIssueTypeFieldValues(latestIssueTypeFieldValues?.uuid ?? ""),
	)
	const navigate = useNavigate()
	const openLocationPicker = useLocationPicker()
	const deleteIssueById = useDeleteIssue()
	const isUserAtleastBasicInProject = useUserOrganizationAccess(OrganizationAccessLevel.BASIC)
	const assets = useShallowEqaulSelector(selectAssetsOfProject(activeProjectId))
	const assetsAndIdentifiers = useAssetsWithIdentifiers(assets)
	const forms = useShallowEqaulSelector(selectFormsOfOrganization(activeOrganizationId))
	const issueAssociations = useAppSelector(selectIssueAssociationsOfIssue(issue.uuid))
	const formSubmissions = useShallowEqaulSelector(selectFormSubmissionsOfIssue(issue.uuid))
	const projectFormSubmissions = useShallowEqaulSelector(selectFormSubmissionsOfProject(activeProjectId))
	const formSubmissionsAndIdentifiers = useFormSubmissionsWithIdentifiers(projectFormSubmissions)
	const attachments = useShallowEqaulSelector(selectAttachmentsOfIssue(issue.uuid))
	const issueTypeIdentifiers = useShallowEqaulSelector(selectIssueTypeIdentifiersOfIssueType(issueType.uuid))
	const issueTypePriorities = useShallowEqaulSelector(selectIssueTypePrioritiesOfIssueType(issueType.uuid))
	const allUsers = useAssignees(useShallowEqaulSelector(selectUsersOfProject(activeProjectId)))
	const issueTypeStatuses = useShallowEqaulSelector(selectIssueTypeStatusesOfIssueType(issueType.uuid))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const [showFormCommandMenu, openFormCommandMenu, closeFormCommandMenu] = useOpenState(false)

	const {
		assignee,
		issueTypePriority,
		issueTypeStatus,
		handleAssigneeChange,
		handlePriorityChange,
		handleStatusChange,
		handleTitleChange,
		handleDescriptionChange,
		handleDueDateChange,
	} = useIssue(issue)

	const handleDeleteIssueAttachment = useCallback(
		(attachment: Stored<IssueAttachment>) => {
			openConfirmDeleteDialog({
				title: "Delete attachment",
				description: "The attachment will be deleted immediately. This action cannot be undone.",
				onAction: () => sdk.issueAttachments.delete(attachment.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleDeleteIssue = useCallback(() => {
		deleteIssueById(issue.uuid, async () => {
			await navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId",
				search: {},
				replace: true,
			})
		})
	}, [deleteIssueById, issue.uuid, navigate])

	const handleOpenLocationPicker = useCallback(() => {
		openLocationPicker((close) => ({
			disableSkip: true,
			onConfirm: (coordinates) => {
				close()
				if (!coordinates) return
				void sdk.issues.update({
					uuid: issue.uuid,
					geo_marker: turf.point(coordinates.toArray()).geometry,
				})
			},
			onCancel: close,
		}))
	}, [issue.uuid, openLocationPicker])

	const handleAttachFiles = useCallback(
		(files: File[]) => {
			return sdk.issueAttachments.bulkAdd(files.map((file) => ({ issue: issue.uuid, file })))
		},
		[issue.uuid],
	)

	const handleTitleTextAreaBlur = useCallback<FocusEventHandler<HTMLTextAreaElement>>(
		(e) => {
			void handleTitleChange(e.target.value)
		},
		[handleTitleChange],
	)

	const handleViewIssue = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/$projectId/issues/$issueId",
			params: (prev) => ({
				...prev,
				issueId: issue.uuid,
			}),
		})
	}, [issue.uuid, navigate])

	const handleLinkAsset = useCallback(
		(asset: Stored<Asset>) => {
			const [_, promise] = sdk.issueAssociations.add({
				asset: asset.uuid,
				issue: issue.uuid,
			})

			void promise.then(() => {
				toast.success({
					title: "Success",
					description: "Successfully linked asset to issue.",
				})
			})
		},
		[issue.uuid],
	)

	const handleViewFormSubmission = useCallback(
		(formSubmission: Stored<FormSubmission>) => {
			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId/forms/submissions/$formSubmissionId",
				params: (prev) => ({ ...prev, formSubmissionId: formSubmission.uuid }),
			})
		},
		[navigate],
	)

	const handleUnlinkFormSubmission = useCallback(
		(formSubmission: Stored<FormSubmission>) => {
			openConfirmDeleteDialog({
				title: "Unlink form submission",
				description: "The form submission will be unlinked. This action cannot be undone.",
				onAction: () => sdk.formSubmissions.update({ uuid: formSubmission.uuid, asset: null, issue: null }),
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleDeleteFormSubmission = useCallback(
		(formSubmission: Stored<FormSubmission>) => {
			openConfirmDeleteDialog({
				title: "Delete form submission",
				description: "The form submission will be deleted immediately. This action cannot be undone.",
				onAction: () => sdk.formSubmissions.delete(formSubmission.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleSelectFormSubmission = useCallback(
		(formSubmission: Stored<FormSubmission>) => {
			const [_, promise] = sdk.formSubmissions.update({
				uuid: formSubmission.uuid,
				asset: null,
				issue: issue.uuid,
			})

			void promise.then(() => {
				toast.success({
					title: "Success",
					description: "Successfully linked form submission to issue.",
				})
			})
		},
		[issue.uuid],
	)

	const handleCreateFormSubmission = useCallback(
		(form: Stored<Form>) => {
			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId/forms/$formId/submit",
				params: (prev) => ({ ...prev, formId: form.uuid }),
				search: {
					issue: issue.uuid,
				},
			})
		},
		[issue.uuid, navigate],
	)

	const issueTitleTextAreaProps = useBlurInput<HTMLTextAreaElement>(issue.title ?? "", handleTitleTextAreaBlur, true)

	const [showPdfExportOptionsDialog, openPdfExportOptionsDialog, closePdfExportOptionsDialog] = useOpenState(false)

	const handleExportIssueToPdf = useCallback(
		(options: IssuePdfExportOptions) => {
			void sdk.issues.getIssuePdf(issueId, options).catch((error: unknown) => {
				toast.error({
					title: "Failed to download PDF",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[issueId],
	)

	const editorContent = useEditorContent(issue.description)
	const handleEditorBlur = useEditorBlur(editorContent, handleDescriptionChange)

	const editor = useEditor(
		{
			content: editorContent,
			editable: isUserAtleastBasicInProject,
			editorProps: {
				attributes: { class: "outline-none h-max min-h-[80px]" },
			},
			onBlur: handleEditorBlur,
			extensions: [
				...BASE_EXTENSIONS,
				...MENTION_EXTENSIONS,
				...LINK_NODES,
				IssueAttachmentNode,
				IssueFileHandler.configure({
					uuid: issue.uuid,
				}),
				Placeholder.configure({
					placeholder: "Enter description",
				}),
			],
		},
		[issue.uuid, isUserAtleastBasicInProject, handleDescriptionChange],
	)

	const handleInsertAttachments = useCallback(
		(chainedCommands: () => ChainedCommands, files: File[]) => {
			void handleAttachFiles(files).then(([offlineAttachments]) => {
				chainedCommands()
					.insertContent(
						offlineAttachments.map((offlineAttachment) => ({
							type: BaseAttachmentNode.name,
							attrs: {
								uuid: offlineAttachment.uuid,
							},
						})),
					)
					.run()
			})
		},
		[handleAttachFiles],
	)

	const issueTypeFields = useShallowEqaulSelector(selectIssueTypeFieldsOfIssueType(issueType.uuid))
	const currentIssueTypeFields = useAppSelector(
		selectIssueTypeFieldsById(latestIssueTypeFieldValues?.fields_revision ?? ""),
	)
	const [computedIssueTypeFields, setComputedIssueTypeFields] = useSyncedState(
		currentIssueTypeFields ?? latestIssueTypeFields,
	)

	const fieldSchema = useDeserializeFieldSchema(computedIssueTypeFields?.fields ?? EMPTY_ARRAY)

	const isIssueTypeFieldValuesDraft = getIsFieldValuesDraft(latestIssueTypeFieldValues)

	const handleIssueTypeFieldValuesChange = useFieldValuesChangeDebounced(
		(allValues: FieldValues) => {
			if (!computedIssueTypeFields?.uuid) return

			if (!isIssueTypeFieldValuesDraft || !latestIssueTypeFieldValues?.uuid) {
				const [values, filesAndPromises] = fieldSchema.extractFilesAndPromisesFromFieldValues(allValues)

				const [offlineFieldValues, _promise] = sdk.issueTypeFieldValues.add({
					fields_revision: computedIssueTypeFields.uuid,
					issue: issue.uuid,
					values: fieldSchema.serializeValues(values),
					published_at: null,
				})

				if (Object.keys(filesAndPromises).length !== 0) {
					void sdk.issueTypeFieldValuesAttachments.bulkAdd(
						getAttachmentPayloadsFromFilesAndPromises(
							offlineFieldValues.uuid,
							filesAndPromises,
							latestIssueTypeFieldValuesAttachments,
						),
					)
				}
			} else {
				const [values, filesAndPromises] = fieldSchema.extractFilesAndPromisesFromFieldValues(allValues)

				const [offlineFieldValues, _promise] = sdk.issueTypeFieldValues.update({
					uuid: latestIssueTypeFieldValues.uuid,
					fields_revision: computedIssueTypeFields.uuid,
					values: fieldSchema.serializeValues(values),
				})

				const attachmentsToDelete = getFieldValuesAttachmentsToDelete(
					latestIssueTypeFieldValuesAttachments,
					filesAndPromises,
				)

				const filesAndPromisesToCreate = getFieldFilesAndPromisesToCreate(
					latestIssueTypeFieldValuesAttachments,
					filesAndPromises,
				)

				if (attachmentsToDelete.length !== 0) {
					void sdk.issueTypeFieldValuesAttachments.bulkDelete(toUuidArray(attachmentsToDelete))
				}

				if (Object.keys(filesAndPromisesToCreate).length !== 0) {
					void sdk.issueTypeFieldValuesAttachments.bulkAdd(
						getAttachmentPayloadsFromFilesAndPromises(
							offlineFieldValues.uuid,
							filesAndPromisesToCreate,
							latestIssueTypeFieldValuesAttachments,
						),
					)
				}
			}
		},
		[
			computedIssueTypeFields?.uuid,
			isIssueTypeFieldValuesDraft,
			issue.uuid,
			fieldSchema,
			latestIssueTypeFieldValues?.uuid,
			latestIssueTypeFieldValuesAttachments,
		],
	)

	const handleIssueTypeFormFieldsSubmit = useCallback(
		(_values: FieldValues) => {
			if (!latestIssueTypeFieldValues?.uuid) return

			handleIssueTypeFieldValuesChange.flush()

			const [_offlineIssueTypeFieldValues, _promise] = sdk.issueTypeFieldValues.update({
				uuid: latestIssueTypeFieldValues.uuid,
				published_at: new Date().toISOString(),
			})
		},
		[latestIssueTypeFieldValues?.uuid, handleIssueTypeFieldValuesChange],
	)

	const handleDeleteIssueTypeFieldValues = useCallback(() => {
		if (!latestIssueTypeFieldValues?.uuid || !isIssueTypeFieldValuesDraft) return

		openConfirmDeleteDialog({
			title: "Delete draft",
			description: "Are you sure you want to delete this draft? This action cannot be undone.",
			onAction: () => {
				handleIssueTypeFieldValuesChange.flush()
				void sdk.issueTypeFieldValues.delete(latestIssueTypeFieldValues.uuid)
			},
		})
	}, [
		isIssueTypeFieldValuesDraft,
		latestIssueTypeFieldValues?.uuid,
		openConfirmDeleteDialog,
		handleIssueTypeFieldValuesChange,
	])

	const handleIssueTypeFieldsSelect = useCallback(
		(fields: Stored<IssueTypeFields>) => {
			handleIssueTypeFieldValuesChange.flush()
			setComputedIssueTypeFields(fields)
		},
		[handleIssueTypeFieldValuesChange, setComputedIssueTypeFields],
	)

	const fieldValues = useDeserializeFieldValues(fieldSchema, latestIssueTypeFieldValues?.values)

	const fieldValuesWithAttachments = useHydrateFieldValuesWithAttachments(
		fieldSchema,
		fieldValues,
		latestIssueTypeFieldValuesAttachments,
		sdk.issueTypeFieldValuesAttachments,
	)

	const sortedFormSubmissions = useSort(formSubmissions, submittedAtModelSortFn)

	const linksCount = issueAssociations.length
	const attachmentsCount = attachments.length + sortedFormSubmissions.length

	return (
		<>
			<div className="flex size-full flex-col gap-2 overflow-hidden p-4">
				<div className="flex flex-wrap justify-between">
					<div className="flex min-w-0 grow items-center gap-2">
						<div className="flex min-w-0 items-center gap-2">
							<IssueTypeIcon issueTypeId={issueType.uuid} />
							<Text className="truncate">{issueType.name}</Text>
							<IssueIndexLabel issueId={issue.uuid} accentColor="base" />
							<IssueTypeDescriptionDialog issueType={issueType} size="sm">
								<IconButton variant="ghost" accentColor="base" size="sm">
									<LuIcon icon={modelIconMapping.info} />
								</IconButton>
							</IssueTypeDescriptionDialog>
						</div>
					</div>
					<ButtonGroup
						className="flex items-center justify-end gap-2"
						size="sm"
						variant="soft"
						accentColor="base"
					>
						<Menu.Root>
							<Menu.ClickTrigger>
								<IconButton aria-label="more" variant="ghost">
									<LuIcon icon={modelIconMapping.more} />
								</IconButton>
							</Menu.ClickTrigger>
							<Menu.Content size="sm">
								<Menu.Pages defaultPage="initial">
									<Menu.PageContent page="initial">
										<Menu.Item onSelect={handleViewIssue}>
											<LuIcon icon={modelIconMapping.expand} />
											View
										</Menu.Item>
										<Menu.Item onSelect={openFormCommandMenu}>
											<LuIcon icon={modelIconMapping.form} />
											Link form
										</Menu.Item>
										<FileInputMenuItem onAttachFiles={handleAttachFiles} />
										<Menu.PageTrigger page="location">
											<LuIcon icon={modelIconMapping.location} />
											Location
										</Menu.PageTrigger>
										<Menu.Item onSelect={openPdfExportOptionsDialog}>
											<LuIcon icon={modelIconMapping.pdf} />
											Export to PDF
										</Menu.Item>
										<OrganizationAccessRequired
											accessLevel={OrganizationAccessLevel.BASIC}
											type="hide"
										>
											<Menu.Separator />
											<Menu.Item onSelect={handleDeleteIssue}>
												<LuIcon icon={modelIconMapping.delete} />
												Delete
											</Menu.Item>
										</OrganizationAccessRequired>
									</Menu.PageContent>
									<Menu.PageContent page="location">
										<LocationGroup
											coordinates={issue.geo_marker?.coordinates}
											onLocationChange={handleOpenLocationPicker}
										/>
									</Menu.PageContent>
								</Menu.Pages>
							</Menu.Content>
						</Menu.Root>
					</ButtonGroup>
				</div>
				<SegmentedTabs.Root
					defaultValue={IssueDetailsViewerTabs.OVERVIEW}
					className="flex grow flex-col gap-4 overflow-hidden"
				>
					<SegmentedTabs.List size="sm" accentColor="base" variant="surface">
						<SegmentedTabs.Trigger className="basis-1" value={IssueDetailsViewerTabs.OVERVIEW}>
							Overview
						</SegmentedTabs.Trigger>
						<SegmentedTabs.Trigger className="basis-1" value={IssueDetailsViewerTabs.ATTACHMENTS}>
							Attachments&nbsp;
							{attachmentsCount + linksCount > 0 && `(${attachmentsCount + linksCount})`}
						</SegmentedTabs.Trigger>
					</SegmentedTabs.List>
					<SegmentedTabs.Content
						value={IssueDetailsViewerTabs.OVERVIEW}
						className="flex grow flex-col gap-4 overflow-y-auto"
					>
						<div className="flex flex-col gap-2">
							<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="disable">
								<textarea
									className="field-sizing-content placeholder-(--base-a9) resize-none text-lg font-bold outline-none"
									placeholder="Enter a title"
									maxLength={TITLE_MAX_LENGTH}
									{...issueTitleTextAreaProps}
								/>
							</OrganizationAccessRequired>

							<EditorContext value={editor}>
								<EditorContent editor={editor} />
								<TextBubbleMenu editor={editor} />
								<NewLineFloatingMenu editor={editor} onAttachFiles={handleInsertAttachments} />
							</EditorContext>

							<ButtonGroup
								className="flex w-full flex-wrap items-center gap-2"
								size="sm"
								variant="soft"
								accentColor="base"
							>
								<DayPickerPopover
									mode="single"
									required={false}
									selected={issue?.due_date ? new Date(issue.due_date) : undefined}
									onSelect={handleDueDateChange}
									variant="solid"
									size="sm"
								>
									<OrganizationAccessRequired
										accessLevel={OrganizationAccessLevel.BASIC}
										type="disable"
									>
										<Button className="w-max">
											<LuIcon icon={modelIconMapping.dueDate} />
											{issue.due_date ? formatCompactDate(issue.due_date) : "Due date"}
										</Button>
									</OrganizationAccessRequired>
								</DayPickerPopover>

								<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="disable">
									<AssigneeSelect
										required
										value={assignee ?? null}
										onValueChange={handleAssigneeChange}
										users={allUsers}
										size="sm"
									>
										<Button>
											{assignee ? (
												<>
													<UserIcon size="xs" userId={assignee.id} />
													<Text className="truncate">{getUserDisplayLabel(assignee)}</Text>
												</>
											) : (
												<>
													<LuIcon icon={modelIconMapping.unassigned} />
													Unassigned
												</>
											)}
										</Button>
									</AssigneeSelect>
								</OrganizationAccessRequired>
								<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="disable">
									<IssuePrioritySelect
										required
										value={issueTypePriority}
										onValueChange={handlePriorityChange}
										priorities={issueTypePriorities}
										size="sm"
									>
										<Button>
											<IssuePriorityIcon priority={issue.priority} />
											{issueTypePriority.name}
										</Button>
									</IssuePrioritySelect>
								</OrganizationAccessRequired>
								<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="disable">
									<IssueTypeStatusSelect
										required
										value={issueTypeStatus}
										onValueChange={handleStatusChange}
										issueTypeStatuses={issueTypeStatuses}
										size="sm"
									>
										<Button>
											<IssueTypeStatusIcon issueTypeStatusId={issue.status} />
											{issueTypeStatus?.name}
										</Button>
									</IssueTypeStatusSelect>
								</OrganizationAccessRequired>
								<Button onClick={openFormCommandMenu}>
									<LuIcon icon={modelIconMapping.form} />
									Link form
								</Button>
							</ButtonGroup>
						</div>

						{issueTypeIdentifiers.length > 0 && (
							<IssueTypeIdentifiersList issue={issue} issueTypeIdentifiers={issueTypeIdentifiers} />
						)}

						{!!computedIssueTypeFields && (
							<>
								<Card className="flex flex-col gap-4" variant="soft" size="sm">
									<FieldsRevisionDropdownSelect
										required
										disabled={isIssueTypeFieldValuesDraft}
										fieldsRevisions={issueTypeFields}
										current={currentIssueTypeFields}
										value={computedIssueTypeFields}
										onValueChange={handleIssueTypeFieldsSelect}
										size="sm"
									>
										<Button
											size="xs"
											accentColor="base"
											variant="surface"
											className="w-max"
											disabled={isIssueTypeFieldValuesDraft}
										>
											Revision {computedIssueTypeFields.revision}
											<LuIcon icon={modelIconMapping.chevronDown} />
										</Button>
									</FieldsRevisionDropdownSelect>

									<IssueTypeFieldsSubmitter
										size="sm"
										key={latestIssueTypeFieldValues?.uuid}
										issueTypeFields={computedIssueTypeFields}
										onSubmit={handleIssueTypeFormFieldsSubmit}
										onValuesChange={handleIssueTypeFieldValuesChange}
										values={fieldValuesWithAttachments}
										hideCancel={!isIssueTypeFieldValuesDraft}
										cancelText="Draft"
										cancelIcon={modelIconMapping.delete}
										onCancel={handleDeleteIssueTypeFieldValues}
										disabled={!isUserAtleastBasicInProject}
										hideSubmit={!isIssueTypeFieldValuesDraft}
									/>
								</Card>
							</>
						)}

						<Separator size="full" />

						<IssueTimeline issue={issue} size="xs" />
					</SegmentedTabs.Content>
					<SegmentedTabs.Content
						value={IssueDetailsViewerTabs.ATTACHMENTS}
						className="flex grow flex-col gap-4 overflow-y-auto"
					>
						<div className="flex flex-col gap-2">
							<div className="flex justify-between gap-2">
								<Text size="sm">Assets</Text>
								<AssetCommandMenu
									assetsAndIdentifiers={assetsAndIdentifiers}
									onSelectAsset={handleLinkAsset}
								>
									<IconButton size="xs" accentColor="base" variant="soft">
										<LuIcon icon={modelIconMapping.add} />
									</IconButton>
								</AssetCommandMenu>
							</div>

							<IssueAssociationList issueAssociations={issueAssociations} type="asset" />
						</div>

						<Separator size="full" />

						<div className="flex flex-col gap-2">
							<div className="flex justify-between gap-2">
								<Text size="sm">Forms</Text>
								<IconButton onClick={openFormCommandMenu} size="xs" accentColor="base" variant="soft">
									<LuIcon icon={modelIconMapping.add} />
								</IconButton>
							</div>

							<FormSubmissionList
								formSubmissions={sortedFormSubmissions}
								onClickFormSubmission={handleViewFormSubmission}
								onViewFormSubmission={handleViewFormSubmission}
								onUnlinkFormSubmission={handleUnlinkFormSubmission}
								onDeleteFormSubmission={handleDeleteFormSubmission}
							/>
						</div>

						<Separator size="full" />

						<section className="flex h-max w-full flex-col gap-2">
							<div className="flex h-max w-full items-center justify-between">
								<Text size="sm">Attachments</Text>
								<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
									<AttachmentButton
										icon
										accentColor="base"
										size="xs"
										variant="soft"
										onAttachFiles={handleAttachFiles}
									>
										<LuIcon icon={modelIconMapping.add} />
									</AttachmentButton>
								</OrganizationAccessRequired>
							</div>

							<AttachmentList
								attachments={attachments}
								onDelete={handleDeleteIssueAttachment}
								service={sdk.issueAttachments}
							/>
						</section>
					</SegmentedTabs.Content>
				</SegmentedTabs.Root>
			</div>

			<FormSubmissionCommandMenu
				open={showFormCommandMenu}
				onOpenChange={closeFormCommandMenu}
				formSubmissionsAndIdentifiers={formSubmissionsAndIdentifiers}
				forms={forms}
				onSelectFormSubmission={handleSelectFormSubmission}
				onCreateFormSubmission={handleCreateFormSubmission}
			/>
			<IssuePdfExportOptionsDialog
				key={issueType.uuid}
				issueTypeId={issueType.uuid}
				onSubmitOptions={handleExportIssueToPdf}
				open={showPdfExportOptionsDialog}
				onOpenChange={closePdfExportOptionsDialog}
			/>
		</>
	)
})

IssueDetailsViewer.displayName = "IssueDetailsViewer"
